import { QueryState } from '_metronic/helpers'
import {
  PlainHeader,
  sortableColumn,
  StackedCell,
  StatusDot,
  StatusDotTone,
  TableColumn,
} from 'app/components'
import {
  EsusEnvioListItem,
  EsusEnvioStatus,
  rotuloStatus,
  rotuloTipoFicha,
} from '../../../core/_models'
import { VAZIO, formatarDataHora } from '../../../core/_formato'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { EsusEnvioActionsCell } from './EsusEnvioActionsCell'

/**
 * O cabeçalho ordenável é tipado sobre `QueryState` (sem `params`), e o nosso provider usa
 * `QueryState<EsusEnvioFiltros>`. O cabeçalho só lê e escreve ordenação/paginação — nunca `params` —,
 * então o adaptador estreita o tipo aqui em vez de tornar o componente compartilhado genérico.
 *
 * Ordenar preserva os filtros: o cabeçalho faz `updateState({...initialQueryState, sort, order})`
 * e `initialQueryState` não tem a chave `params`, então o spread não a apaga.
 */
const useQueryRequestOrdenacao = () => {
  const { state, updateState } = useQueryRequest()
  return {
    state: state as QueryState,
    updateState: updateState as (novoEstado: Partial<QueryState>) => void,
  }
}

/** Linha da tabela no formato que o react-table entrega à `Cell`. */
type CelulaProps = { data: readonly EsusEnvioListItem[]; row: { index: number } }
const linha = ({ data, row }: CelulaProps) => data[row.index]

/**
 * Tom do status: texto + ponto (o texto carrega o significado, a cor só reforça). Mesma leitura
 * dos badges `badge-light-*` usados no detalhe (`CLASSE_BADGE_STATUS`).
 */
const TOM_STATUS: Record<EsusEnvioStatus, StatusDotTone> = {
  PENDENTE: 'warning',
  ENVIANDO: 'info',
  ENVIADO: 'success',
  ERRO: 'danger',
}

/**
 * Colunas da listagem de envios.
 *
 * A tabela é `table-layout: fixed`: as colunas curtas têm largura e Instituição e Paciente dividem o
 * resto — somadas, as larguras fixas (~720px) cabem no card sem rolagem horizontal. Unidade, data
 * do atendimento, tentativas e UUID ficam na linha expandida (`EsusEnvioExpandedRow`).
 */
export const esusEnviosColumns: ReadonlyArray<TableColumn<EsusEnvioListItem>> = [
  // Coluna do botão de expandir (o ModernTable desenha o botão na coluna de id `expand`).
  {
    Header: () => <span className='visually-hidden'>Detalhes</span>,
    id: 'expand',
    cellWidth: '56px',
  },
  {
    /*
      A lista é combinada entre as empresas autorizadas, então a primeira coluna diz de quem é a
      linha. Sem ordenação de propósito: o campo de `sort` seria `empresa.nome`, enquanto a célula
      mostra `nomeFantasia` — a ordem não bateria com o que está na tela. Ordenar por
      unidade/paciente/data do atendimento já é um defeito à parte, anterior a esta tela (ver Dev
      Evidence).
    */
    Header: () => <PlainHeader>Instituição</PlainHeader>,
    id: 'empresaNome',
    Cell: (props: CelulaProps) => <StackedCell primary={linha(props).empresaNome || VAZIO} />,
  },
  /*
    Vem antes de "Atendimento" de propósito: é a coluna que explica por que a linha seguinte está
    vazia numa ficha de cadastro (a FCI é por cidadão e não tem atendimento, por desenho da F7a).

    Ordenação habilitada: o `sort=tipoFicha` existe na entidade (`EsusEnvio.tipoFicha`) e a ordem do
    valor persistido (`A` < `C` < `P`) coincide com a dos rótulos (Atendimento individual < Cadastro
    individual < Procedimentos) — conferido de novo com a ficha de procedimentos na F3, e continua
    sem o descasamento que obrigou a desligar o sort em `empresaNome`.
  */
  sortableColumn<EsusEnvioListItem>({
    id: 'tipoFicha',
    cellWidth: '160px',
    useQueryRequest: useQueryRequestOrdenacao,
    fields: [{ field: 'tipoFicha', label: 'Tipo de ficha' }],
    Cell: (props: CelulaProps) => <StackedCell primary={rotuloTipoFicha(linha(props))} />,
  }),
  sortableColumn<EsusEnvioListItem>({
    id: 'atendimentoId',
    cellWidth: '160px',
    useQueryRequest: useQueryRequestOrdenacao,
    fields: [{ field: 'atendimentoId', label: 'Atendimento' }],
    Cell: (props: CelulaProps) => (
      <StackedCell tabular primary={linha(props).atendimentoProtocolo ?? VAZIO} />
    ),
  }),
  sortableColumn<EsusEnvioListItem>({
    id: 'pacienteNome',
    useQueryRequest: useQueryRequestOrdenacao,
    fields: [{ field: 'pacienteNome', label: 'Paciente' }],
    Cell: (props: CelulaProps) => <StackedCell primary={linha(props).pacienteNome || VAZIO} />,
  }),
  sortableColumn<EsusEnvioListItem>({
    id: 'status',
    cellWidth: '130px',
    useQueryRequest: useQueryRequestOrdenacao,
    fields: [{ field: 'status', label: 'Status' }],
    Cell: (props: CelulaProps) => {
      const envio = linha(props)
      return (
        <StatusDot
          label={rotuloStatus(envio)}
          tone={TOM_STATUS[envio.status as EsusEnvioStatus] ?? 'neutral'}
        />
      )
    },
  }),
  sortableColumn<EsusEnvioListItem>({
    id: 'dhEnvio',
    cellWidth: '140px',
    hideOnMobile: true,
    useQueryRequest: useQueryRequestOrdenacao,
    fields: [{ field: 'dhEnvio', label: 'Enviado em' }],
    Cell: (props: CelulaProps) => (
      <StackedCell tabular primary={formatarDataHora(linha(props).dhEnvio)} />
    ),
  }),
  {
    Header: () => <PlainHeader>Ações</PlainHeader>,
    id: 'actions',
    cellWidth: '72px',
    align: 'end',
    Cell: (props: CelulaProps) => <EsusEnvioActionsCell envio={linha(props)} />,
  },
]
